// components/kursi-play-v2/KpLiveWorldHomeView.tsx
// =============================================================================
// Kursi Play V2 — World Home Screen
// Shows intro narrative, lesson track, XP + streak.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { router, usePathname } from 'expo-router';
import { theme } from '@/constants/theme';
import { getThemeConfig } from '@/constants/kursiPlayThemes';
import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import { KpGuestMergeBanner } from '@/components/kursi-play-v2/shared/KpGuestMergeBanner';
import {
  buildRuntimeExamPath,
  buildRuntimeLessonPath,
  buildRuntimePracticePath,
  buildRuntimeReviewPath,
  buildRuntimeReversePath,
  buildRuntimeTutorPath,
  buildRuntimeFlashcardsPath,
  buildRuntimeStudyPlanPath,
  buildRuntimeCommunityPath,
  resolveRuntimeBase,
} from '@/features/unified-learning/runtime/runtimeRoutes';
import { AnimatedList, ClickSpark, ElectricBorder } from '@/components/learn-premium/PremiumPrimitives';
import { trackKursiPlayEvent } from '@/utils/kursiPlayTelemetry';
import { LEARN_LAUNCH_CONFIG } from '@/constants/learnLaunchConfig';
import type {
  KpV2WorldHomeData,
  KpV2LessonWithStatus,
  KpV2LeaderboardRow,
  KpV2DailyMission,
} from '@/types/kursi-play-v2';

// ── Design tokens ─────────────────────────────────────────────────────────────

const PRIMARY    = theme.colors.primary;
const BG         = '#0B0B14';
const SURFACE    = '#16142A';
const SURFACE2   = '#221F3D';
const SURFACE3   = '#2D2B50';
const TEXT       = '#F0EFF8';
const TEXT_MED   = '#9896B4';
const TEXT_MUTED = '#5E5C78';
const GREEN      = '#10B981';
const GOLD       = '#F59E0B';

const LESSON_STATUS: Record<string, { bg: string; border: string; dot: string; text: string }> = {
  completed: { bg: '#0B2618', border: `${GREEN}55`, dot: GREEN,    text: GREEN },
  current:   { bg: `${PRIMARY}15`, border: PRIMARY,       dot: PRIMARY,   text: TEXT },
  locked:    { bg: SURFACE2,       border: SURFACE3,      dot: TEXT_MUTED, text: TEXT_MUTED },
};

// ── Props ─────────────────────────────────────────────────────────────────────

type Props = { slug: string; userId: string; guestSessionToken?: string };
type ActionTileTone = 'primary' | 'standard' | 'quiet';
type ActionTile = {
  key: string;
  title: string;
  description: string;
  cta: string;
  icon: string;
  tone?: ActionTileTone;
  onPress: () => void;
};

// ── Main component ─────────────────────────────────────────────────────────────

export function KpLiveWorldHomeView({ slug, userId, guestSessionToken }: Props) {
  const runtimeSession = useMemo(() => ({ uid: userId, token: guestSessionToken }), [userId, guestSessionToken]);
  const [data, setData]       = useState<KpV2WorldHomeData | null>(null);
  const runtimeBase = resolveRuntimeBase(usePathname());
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [introDismissed, setIntroDismissed] = useState(false);

  const fadeAnim  = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(16)).current;

  const loadHome = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await kpLiveApi.getWorldHome(slug, userId);
      setData(result);
      setIntroDismissed(!!result.progress.intro_dismissed_at);
      Animated.parallel([
        Animated.timing(fadeAnim,  { toValue: 1, duration: 220, useNativeDriver: true }),
        Animated.timing(slideAnim, { toValue: 0, duration: 220, useNativeDriver: true }),
      ]).start();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינה');
    } finally {
      setLoading(false);
    }
  }, [slug, userId, fadeAnim, slideAnim]);

  useEffect(() => { loadHome(); }, [loadHome]);

  const handleDismissIntro = useCallback(async () => {
    setIntroDismissed(true);
    try { await kpLiveApi.dismissIntro(slug, userId); } catch { /* non-fatal */ }
  }, [slug, userId]);

  const handleStartLesson = useCallback((lesson: KpV2LessonWithStatus) => {
    if (lesson.status === 'locked') return;
    router.push(buildRuntimeLessonPath(runtimeBase, slug, lesson.id, runtimeSession) as any);
  }, [runtimeBase, slug, runtimeSession]);

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <View style={s.centered}>
        <ActivityIndicator color={PRIMARY} size="large" />
        <Text style={s.loadingText}>טוען מסלול...</Text>
      </View>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────

  if (error || !data) {
    return (
      <View style={s.centered}>
        <Text style={s.errEmoji}>⚠</Text>
        <Text style={s.errTitle}>{error ?? 'שגיאה בטעינה'}</Text>
        <Pressable style={s.retryBtn} onPress={loadHome} accessibilityRole="button">
          <Text style={s.retryBtnText}>נסה שוב</Text>
        </Pressable>
        <Pressable style={s.backHomeBtn} onPress={() => router.replace('/learn' as any)} accessibilityRole="button">
          <Text style={s.backHomeBtnText}>חזרה ל-KURSI LEARN</Text>
        </Pressable>
      </View>
    );
  }

  // ── Data ───────────────────────────────────────────────────────────────────

  const { world, lessons, progress, leaderboard, daily_mission } = data;
  const completionPct = progress.total_lessons > 0
    ? Math.round((progress.completed_lessons / progress.total_lessons) * 100)
    : 0;
  const themeConfig  = getThemeConfig(world.visual_theme);
  const accent       = world.primary_color ?? PRIMARY;
  const nextLesson   = lessons.find((l) => l.status === 'current');
  const hasFirstLessonMilestone = progress.completed_lessons > 0;
  const hasFirstPracticeMilestone = progress.total_xp_earned >= 40;
  const examUnlocked = hasFirstLessonMilestone || hasFirstPracticeMilestone;
  const heroCtaLabel = nextLesson ? 'המשך ללמוד עכשיו' : 'התחל תרגול קצר';
  const heroCtaAction = nextLesson
    ? () => {
        void trackKursiPlayEvent('learn_lesson_started', { slug, uid: userId, source: 'home_primary_cta' });
        handleStartLesson(nextLesson);
      }
    : () => {
        void trackKursiPlayEvent('learn_practice_started', { slug, uid: userId, source: 'home_primary_cta' });
        router.push(buildRuntimePracticePath(runtimeBase, slug, runtimeSession) as any);
      };

  const secondaryActions: ActionTile[] = [
    {
      key: 'practice',
      title: 'מרכז תרגול',
      description: 'תרגול ממוקד לפי חולשות כדי לחזק ביצועים לפני השיעור הבא.',
      cta: 'התחל תרגול',
      icon: '◈',
      onPress: () => {
        void trackKursiPlayEvent('learn_practice_started', { slug, uid: userId, source: 'home_secondary' });
        router.push(buildRuntimePracticePath(runtimeBase, slug, runtimeSession) as any);
      },
    },
    {
      key: 'review',
      title: 'סקירת טעויות',
      description: 'מה נפל, למה זה קרה, ומה לתקן עכשיו.',
      cta: 'פתח סקירה',
      icon: '↺',
      onPress: () => {
        void trackKursiPlayEvent('learn_review_entered', { slug, uid: userId, source: 'home_secondary' });
        router.push(buildRuntimeReviewPath(runtimeBase, slug, runtimeSession) as any);
      },
    },
  ];

  const advancedActions: ActionTile[] = [
    {
      key: 'exam',
      title: 'מצב מבחן',
      description: 'שלב ג: בדיקת מוכנות אחרי שיעור + תרגול + סקירה.',
      cta: examUnlocked ? 'התחל מבחן מוכנות' : 'השלימו שיעור או תרגול כדי לפתוח',
      icon: '▣',
      tone: 'primary',
      onPress: () => {
        if (!examUnlocked) return;
        void trackKursiPlayEvent('learn_exam_entered', { slug, uid: userId, source: 'home_advanced' });
        router.push(buildRuntimeExamPath(runtimeBase, slug, runtimeSession) as any);
      },
    },
    ...(LEARN_LAUNCH_CONFIG.advancedTools.reverse ? [{
      key: 'reverse',
      title: 'חזרה ממוקדת',
      description: 'מתי להשתמש: כשיש טעויות חוזרות שצריך לסגור לפני מבחן.',
      cta: 'פתח חזרה ממוקדת',
      icon: '⊕',
      tone: 'quiet' as const,
      onPress: () => router.push(buildRuntimeReversePath(runtimeBase, slug, runtimeSession) as any),
    }] : []),
    ...(LEARN_LAUNCH_CONFIG.advancedTools.tutor ? [{
      key: 'tutor',
      title: 'מנחה אישי',
      description: 'מתי להשתמש: כשתקועים בשאלה ספציפית אחרי תרגול.',
      cta: 'פתח מנחה',
      icon: '◎',
      tone: 'quiet' as const,
      onPress: () => router.push(buildRuntimeTutorPath(runtimeBase, slug, runtimeSession, { worldId: world.id as string }) as any),
    }] : []),
    ...(LEARN_LAUNCH_CONFIG.advancedTools.flashcards ? [{
      key: 'flashcards',
      title: 'כרטיסיות חזרה',
      description: 'מתי להשתמש: לחיזוק זכירה קצר בין סשני תרגול.',
      cta: 'פתח כרטיסיות',
      icon: '◧',
      tone: 'quiet' as const,
      onPress: () => router.push(buildRuntimeFlashcardsPath(runtimeBase, slug, runtimeSession, { worldId: world.id as string }) as any),
    }] : []),
    ...(LEARN_LAUNCH_CONFIG.advancedTools.studyPlan ? [{
      key: 'plan',
      title: 'תוכנית לימוד',
      description: 'מתי להשתמש: לבניית תוכנית שבועית קבועה סביב יעד מבחן.',
      cta: 'פתח תוכנית',
      icon: '≡',
      tone: 'quiet' as const,
      onPress: () => router.push(buildRuntimeStudyPlanPath(runtimeBase, slug, runtimeSession, { worldId: world.id as string }) as any),
    }] : []),
    ...(LEARN_LAUNCH_CONFIG.advancedTools.community ? [{
      key: 'community',
      title: 'קהילת לומדים',
      description: 'מתי להשתמש: לשאלות המשך אחרי שניסיתם לפתור לבד.',
      cta: 'היכנסו לקהילה',
      icon: '⊙',
      tone: 'quiet' as const,
      onPress: () => router.push(buildRuntimeCommunityPath(runtimeBase, slug, runtimeSession, { worldId: world.id as string }) as any),
    }] : []),
  ];

  return (
    <View style={s.root}>
      <Animated.View style={{ flex: 1, opacity: fadeAnim, transform: [{ translateY: slideAnim }] }}>
        <ScrollView
          style={s.scroll}
          contentContainerStyle={s.scrollContent}
          showsVerticalScrollIndicator={false}
        >

          {/* ── Domain badge ── */}
          <View style={[s.domainBadge, { borderColor: accent + '3A', backgroundColor: accent + '10' }]}>
            <Text style={s.domainBadgeHero}>{themeConfig.heroEmoji}</Text>
            <Text style={[s.domainBadgeLabel, { color: accent }]}>{themeConfig.environmentLabel}</Text>
            <Text style={s.domainBadgeEnv}>{themeConfig.environmentEmoji}</Text>
          </View>

          {/* ── Hero card ── */}
          <View style={[s.hero, { borderColor: accent + '40' }]}>
            {/* Subtle accent line at top */}
            <View style={[s.heroAccentLine, { backgroundColor: accent }]} />

            <View style={s.heroBody}>
              <Text style={s.worldName}>{world.world_name}</Text>
              {world.character_name && (
                <View style={s.characterRow}>
                  <Text style={s.characterBadge}>
                    {themeConfig.bossEmoji}  {world.character_name}
                    {world.character_role ? ` · ${world.character_role}` : ''}
                  </Text>
                </View>
              )}

              {/* Progress */}
              <View style={s.progressBlock}>
                <View style={s.progressLabelRow}>
                  <Text style={[s.progressPct, { color: accent }]}>{completionPct}%</Text>
                  <Text style={s.progressCountText}>
                    {progress.completed_lessons}/{progress.total_lessons} שיעורים
                  </Text>
                </View>
                <View style={s.progressTrack}>
                  <View style={[s.progressFill, { width: `${completionPct}%` as any, backgroundColor: accent }]} />
                </View>
              </View>

              {/* Stats pills */}
              <View style={s.statsRow}>
                <StatPill accent={accent} icon="★" value={`${progress.total_xp_earned} נק׳`} />
                {progress.current_streak_days > 0 && (
                  <StatPill accent={accent} icon="▲" value={`${progress.current_streak_days} ימים`} />
                )}
                {(progress.freeze_count ?? 0) > 0 && (
                  <StatPill accent="#60A5FA" icon="○" value={`${progress.freeze_count} קפאון`} />
                )}
                <StatPill accent={accent} icon="▷" value={`${lessons.length} שיעורים`} />
              </View>

              <View style={s.heroMissionBlock}>
                <Text style={s.heroMissionEyebrow}>המשימה הבאה שלך</Text>
                <Text style={s.heroMissionTitle}>
                  {nextLesson ? `להמשיך לשיעור: ${nextLesson.title}` : 'לבנות מומנטום עם תרגול קצר'}
                </Text>
                <ClickSpark onPress={heroCtaAction}>
                  <View style={[s.heroCtaBtn, { backgroundColor: accent }]}>
                    <Text style={s.heroCtaText}>{heroCtaLabel} ←</Text>
                  </View>
                </ClickSpark>
              </View>
            </View>
          </View>

          {/* ── Guest banner ── */}
          <KpGuestMergeBanner userId={userId} primaryColor={accent} />

          <View style={s.persistenceCard}>
            <Text style={s.persistenceTitle}>איך ההתקדמות נשמרת</Text>
            <Text style={s.persistenceBody}>ההתקדמות נשמרת למזהה הזה במכשיר. כדי לא לאבד רצף במעבר מכשיר, מומלץ לחבר חשבון ולמזג התקדמות.</Text>
          </View>

          {/* ── Daily mission ── */}
          {daily_mission && (
            <DailyMissionCard
              mission={daily_mission}
              accent={accent}
              onPress={() => nextLesson && handleStartLesson(nextLesson)}
            />
          )}

          {/* ── Intro narrative ── */}
          {!introDismissed && world.narrative_intro && (
            <View style={s.introCard}>
              <View style={s.introHeaderRow}>
                <Text style={[s.introTag, { color: accent }]}>הקדמה למסלול</Text>
              </View>
              <Text style={s.introText}>{world.narrative_intro}</Text>
              {world.learning_goal && (
                <View style={s.goalBox}>
                  <Text style={s.goalLabel}>מטרת הלמידה</Text>
                  <Text style={s.goalText}>{world.learning_goal}</Text>
                </View>
              )}
              <Pressable
                style={[s.dismissBtn, { backgroundColor: accent }]}
                onPress={handleDismissIntro}
                accessibilityRole="button"
              >
                <Text style={s.dismissBtnText}>התחל עכשיו ⟶</Text>
              </Pressable>
            </View>
          )}

          <SectionHeader
            title="שלב A · שיעור"
            subtitle="התחל/המשך שיעור — זה הצעד עם ההשפעה הכי גבוהה"
          />
          <View style={s.primaryFocusCard}>
            <Text style={s.primaryFocusEyebrow}>הצעד הבא שלך</Text>
            <Text style={s.primaryFocusTitle}>
              {nextLesson ? `המשך לשיעור: ${nextLesson.title}` : 'התחל תרגול קצר לבניית מומנטום'}
            </Text>
            <Text style={s.primaryFocusBody}>
              {nextLesson
                ? 'השיעור הבא הוא הפעולה שתיתן את ההתקדמות המשמעותית ביותר כרגע.'
                : 'עדיין אין שיעור נוכחי פתוח, לכן תרגול קצר הוא הדרך הכי טובה להתקדם עכשיו.'}
            </Text>
            <ClickSpark onPress={heroCtaAction}>
              <View style={[s.primaryFocusBtn, { backgroundColor: accent }]}>
                <Text style={s.primaryFocusBtnText}>{heroCtaLabel}</Text>
              </View>
            </ClickSpark>
          </View>

          <SectionHeader
            title="שלב B · תרגול וסקירה"
            subtitle="תרגול ממוקד ואז תיקון טעויות לפני מבחן"
          />
          <View style={s.secondaryGrid}>
            {secondaryActions.map((action) => (
              <ActionTileCard key={action.key} action={action} accent={accent} />
            ))}
          </View>

          <SectionHeader
            title="שלב C · מוכנות למבחן"
            subtitle="המבחן מסכם את השרשרת: שיעור → תרגול → סקירה"
          />
          {examUnlocked ? (
            <View style={s.supportGrid}>
              {advancedActions.map((tool) => (
                <ActionTileCard key={tool.key} action={tool} accent={accent} />
              ))}
            </View>
          ) : (
            <View style={s.emptyLessonsCard}>
              <Text style={s.emptyLessonsTitle}>מבחן נפתח אחרי בסיס למידה</Text>
              <Text style={s.emptyLessonsBody}>השלימו שיעור ראשון או תרגול ראשון כדי לפתוח בדיקת מוכנות.</Text>
            </View>
          )}

          {/* ── Lesson track ── */}
          <SectionHeader
            title="מסלול הלמידה"
            subtitle="התקדמות לפי שיעורים, שלב אחרי שלב"
          />

          {lessons.length > 0 ? (
            <AnimatedList
              items={lessons}
              renderItem={(lesson, idx) => (
                <LessonRow
                  key={lesson.id}
                  lesson={lesson}
                  idx={idx}
                  accent={accent}
                  onPress={() => handleStartLesson(lesson)}
                />
              )}
            />
          ) : (
            <View style={s.emptyLessonsCard}>
              <Text style={s.emptyLessonsTitle}>עדיין אין שיעורים במסלול הזה</Text>
              <Text style={s.emptyLessonsBody}>
                אפשר להתחיל מתרגול קצר כדי לאבחן רמה, או לחזור למסך יצירת המסלול ולהפיק שיעורים.
              </Text>
              <Pressable
                style={[s.emptyLessonsBtn, { borderColor: accent + '55' }]}
                onPress={() => router.push(buildRuntimePracticePath(runtimeBase, slug, runtimeSession) as any)}
                accessibilityRole="button"
              >
                <Text style={[s.emptyLessonsBtnText, { color: accent }]}>פתח תרגול מהיר</Text>
              </Pressable>
            </View>
          )}

          {/* ── Leaderboard ── */}
          {leaderboard && leaderboard.length > 1 && (
            <LeaderboardCard rows={leaderboard} accent={accent} />
          )}

          <View style={{ height: 48 }} />
        </ScrollView>
      </Animated.View>
    </View>
  );
}

// ── DailyMissionCard ──────────────────────────────────────────────────────────

function DailyMissionCard({
  mission, accent, onPress,
}: {
  mission: KpV2DailyMission; accent: string; onPress: () => void;
}) {
  const progressAnim = useRef(new Animated.Value(0)).current;
  const pct = mission.target > 0 ? Math.min(1, mission.progress / mission.target) : 1;
  const isCompleted = mission.status === 'completed';

  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: pct, duration: 800, useNativeDriver: false,
    }).start();
  }, []);

  const barWidth = progressAnim.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] });

  return (
    <ElectricBorder>
      <View style={[s.missionCard, { borderColor: accent + '3A' }]}>
      <View style={s.missionHeaderRow}>
        <View style={[s.missionIconWrap, { backgroundColor: accent + '18' }]}>
          <Text style={s.missionIconText}>{isCompleted ? '✓' : '◎'}</Text>
        </View>
        <View style={s.missionTitleBlock}>
          <Text style={s.missionSuperLabel}>משימה יומית</Text>
          <Text style={[s.missionTitle, isCompleted && { color: GREEN }]}>
            {mission.title}
          </Text>
        </View>
        <View style={[s.missionXpBadge, { borderColor: accent + '44' }]}>
          <Text style={[s.missionXpText, { color: accent }]}>+{mission.reward_xp} נק׳</Text>
        </View>
      </View>

      {mission.target > 1 && (
        <View style={s.missionProgressArea}>
          <Text style={s.missionProgressLabel}>{mission.progress}/{mission.target}</Text>
          <View style={s.missionTrack}>
            <Animated.View
              style={[s.missionFill, { width: barWidth as any, backgroundColor: isCompleted ? GREEN : accent }]}
            />
          </View>
        </View>
      )}

      {!isCompleted && (
        <ClickSpark onPress={onPress}>
          <View style={[s.missionCta, { backgroundColor: accent }]}>
            <Text style={s.missionCtaText}>המשך ללמוד ›</Text>
          </View>
        </ClickSpark>
      )}
      </View>
    </ElectricBorder>
  );
}

// ── LessonRow ─────────────────────────────────────────────────────────────────

function LessonRow({
  lesson, idx, accent, onPress,
}: {
  lesson: KpV2LessonWithStatus; idx: number; accent: string; onPress: () => void;
}) {
  const colors   = LESSON_STATUS[lesson.status] ?? LESSON_STATUS.locked;
  const isLocked = lesson.status === 'locked';
  const statusIcon =
    lesson.status === 'completed' ? '✓' :
    lesson.status === 'current'   ? '▶' : String(idx + 1);

  return (
    <Pressable
      style={[s.lessonRow, { backgroundColor: colors.bg, borderColor: colors.border }]}
      onPress={onPress}
      disabled={isLocked}
      accessibilityRole="button"
      accessibilityLabel={`שיעור ${idx + 1}: ${lesson.title}`}
      accessibilityState={{ disabled: isLocked }}
    >
      {/* Status circle */}
      <View style={[s.lessonDot, { borderColor: colors.dot, backgroundColor: colors.dot + '18' }]}>
        <Text style={[s.lessonDotIcon, { color: colors.dot }]}>{statusIcon}</Text>
      </View>

      {/* Content */}
      <View style={s.lessonContent}>
        <Text style={[s.lessonTitle, { color: colors.text }, isLocked && s.lessonTitleLocked]} numberOfLines={2}>
          {lesson.title}
        </Text>
        {lesson.description && (
          <Text style={s.lessonDesc} numberOfLines={1}>{lesson.description}</Text>
        )}
        <View style={s.lessonMeta}>
          <Text style={s.lessonMetaItem}>★ {lesson.xp_reward} נק׳</Text>
          {lesson.scenario_count > 0 && (
            <Text style={s.lessonMetaItem}>· {lesson.scenario_count} תרחישים</Text>
          )}
          <Text style={s.lessonMetaItem}>· ~{lesson.estimated_minutes} דק׳</Text>
        </View>
        {lesson.status === 'completed' && lesson.xp_earned > 0 && (
          <Text style={s.lessonEarned}>{lesson.xp_earned} נק׳ ✓</Text>
        )}
      </View>

      {/* Arrow */}
      {!isLocked && (
        <Text style={[s.lessonArrow, { color: colors.dot }]}>›</Text>
      )}
    </Pressable>
  );
}

// ── LeaderboardCard ───────────────────────────────────────────────────────────

function LeaderboardCard({ rows, accent }: { rows: KpV2LeaderboardRow[]; accent: string }) {
  const MEDALS = ['🥇', '🥈', '🥉'];

  return (
    <View style={[s.lbCard, { borderColor: accent + '2A' }]}>
      <Text style={s.lbTitle}>לוח מובילים</Text>
      {rows.map((row) => (
        <View
          key={row.user_id + row.rank}
          style={[
            s.lbRow,
            row.is_current_user && { backgroundColor: accent + '18', borderRadius: 10 },
          ]}
        >
          <Text style={s.lbRank}>{row.rank <= 3 ? MEDALS[row.rank - 1] : `#${row.rank}`}</Text>
          <Text
            style={[s.lbName, row.is_current_user && { color: accent, fontFamily: theme.fonts.bold }]}
            numberOfLines={1}
          >
            {row.display_name}
          </Text>
          <Text style={s.lbXp}>{row.total_xp} XP</Text>
        </View>
      ))}
    </View>
  );
}

// ── StatPill ──────────────────────────────────────────────────────────────────

function StatPill({ icon, value, accent }: { icon: string; value: string; accent: string }) {
  return (
    <View style={[s.statPill, { backgroundColor: accent + '14', borderColor: accent + '28' }]}>
      <Text style={s.statPillIcon}>{icon}</Text>
      <Text style={[s.statPillText, { color: accent }]}>{value}</Text>
    </View>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <View style={s.sectionHeaderWrap}>
      <Text style={s.sectionTitle}>{title}</Text>
      <Text style={s.sectionSubtitle}>{subtitle}</Text>
    </View>
  );
}

function ActionTileCard({ action, accent }: { action: ActionTile; accent: string }) {
  const tone = action.tone ?? 'standard';
  const isPrimary = tone === 'primary';
  const isQuiet = tone === 'quiet';

  return (
    <Pressable
      style={[
        s.actionCard,
        isPrimary && [s.actionCardPrimary, { borderColor: accent + '65', backgroundColor: accent + '10' }],
        isQuiet && s.actionCardQuiet,
      ]}
      onPress={action.onPress}
      accessibilityRole="button"
    >
      <View style={s.actionIconRow}>
        <Text style={s.actionIcon}>{action.icon}</Text>
        {isPrimary && <Text style={[s.actionPriorityTag, { color: accent, borderColor: accent + '55' }]}>מומלץ עכשיו</Text>}
      </View>
      <Text style={s.actionTitle}>{action.title}</Text>
      <Text style={s.actionBody}>{action.description}</Text>
      <Text style={[s.actionCta, { color: isQuiet ? TEXT_MED : accent }]}>{action.cta} ←</Text>
    </Pressable>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  scroll: { flex: 1 },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'web' ? 28 : 20,
    paddingBottom: 16,
    maxWidth: 680,
    alignSelf: 'center' as any,
    width: '100%',
    gap: 12,
  },

  // Loading / Error
  centered: {
    flex: 1, backgroundColor: BG,
    alignItems: 'center', justifyContent: 'center', gap: 14, padding: 32,
  },
  loadingText: { color: TEXT_MUTED, fontFamily: theme.fonts.regular, fontSize: 14, marginTop: 6 },
  errEmoji:   { fontSize: 40, color: TEXT_MUTED },
  errTitle:   { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 16, textAlign: 'center', maxWidth: 280 },
  retryBtn:   {
    backgroundColor: PRIMARY, borderRadius: 12,
    paddingVertical: 13, paddingHorizontal: 28, marginTop: 6,
  },
  retryBtnText: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 14 },
  backHomeBtn: { borderRadius: 12, paddingVertical: 10, paddingHorizontal: 20, marginTop: 4 },
  backHomeBtnText: { color: TEXT_MUTED, fontFamily: theme.fonts.regular, fontSize: 13 },

  // Domain badge
  domainBadge: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 9,
  },
  domainBadgeEnv:   { fontSize: 18 },
  domainBadgeLabel: {
    flex: 1, fontFamily: theme.fonts.bold,
    fontSize: 13, textAlign: 'right', letterSpacing: 0.2,
  },
  domainBadgeHero:  { fontSize: 16 },

  // Hero card
  hero: {
    backgroundColor: SURFACE,
    borderRadius: 20,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 8px 32px rgba(0,0,0,0.5)' } as any) : {}),
  },
  heroAccentLine: { height: 3, width: '100%' },
  heroBody: { padding: 20, gap: 14 },
  worldName: {
    fontFamily: theme.fonts.bold, fontSize: 22, color: TEXT, textAlign: 'right', lineHeight: 30,
  },
  characterRow: { marginTop: -6 },
  characterBadge: {
    fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED, textAlign: 'right',
  },

  // Progress
  progressBlock: { gap: 8 },
  progressLabelRow: {
    flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between',
  },
  progressPct: { fontFamily: theme.fonts.bold, fontSize: 15 },
  progressCountText: { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MUTED },
  progressTrack: {
    height: 6, borderRadius: 99, backgroundColor: SURFACE3, overflow: 'hidden',
  },
  progressFill: { height: 6, borderRadius: 99 },

  // Stats
  statsRow: { flexDirection: 'row-reverse', gap: 8, flexWrap: 'wrap' },
  statPill: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 5,
    borderRadius: 99, borderWidth: 1,
    paddingHorizontal: 12, paddingVertical: 6,
  },
  statPillIcon: { fontSize: 12 },
  statPillText: { fontFamily: theme.fonts.bold, fontSize: 12 },
  heroMissionBlock: {
    marginTop: 4,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: SURFACE3,
    backgroundColor: BG + '66',
    padding: 14,
    gap: 8,
  },
  heroMissionEyebrow: {
    color: TEXT_MUTED,
    fontFamily: theme.fonts.bold,
    fontSize: 11,
    textAlign: 'right',
    letterSpacing: 0.4,
  },
  heroMissionTitle: {
    color: TEXT,
    fontFamily: theme.fonts.bold,
    fontSize: 15,
    textAlign: 'right',
    lineHeight: 22,
  },
  heroCtaBtn: {
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  heroCtaText: {
    color: '#fff',
    fontFamily: theme.fonts.bold,
    fontSize: 14,
  },

  // Daily mission
  missionCard: {
    backgroundColor: SURFACE,
    borderRadius: 18,
    borderWidth: 1,
    padding: 16,
    gap: 12,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 4px 16px rgba(0,0,0,0.35)' } as any) : {}),
  },
  missionHeaderRow: {
    flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 12,
  },
  missionIconWrap: {
    width: 40, height: 40, borderRadius: 12,
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  missionIconText: { fontSize: 18 },
  missionTitleBlock: { flex: 1, gap: 3 },
  missionSuperLabel: {
    fontFamily: theme.fonts.bold, fontSize: 10, color: TEXT_MUTED,
    textAlign: 'right', letterSpacing: 0.8, textTransform: 'uppercase' as any,
  },
  missionTitle: {
    fontFamily: theme.fonts.bold, fontSize: 15, color: TEXT, textAlign: 'right',
  },
  missionXpBadge: {
    borderRadius: 99, borderWidth: 1,
    paddingHorizontal: 10, paddingVertical: 4, flexShrink: 0,
    backgroundColor: 'transparent',
  },
  missionXpText: { fontFamily: theme.fonts.bold, fontSize: 12 },
  missionProgressArea: { gap: 5 },
  missionProgressLabel: {
    fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MUTED, textAlign: 'right',
  },
  missionTrack: { height: 6, borderRadius: 99, backgroundColor: SURFACE3, overflow: 'hidden' },
  missionFill:  { height: 6, borderRadius: 99 },
  missionCta: {
    borderRadius: 12, paddingVertical: 12,
    alignItems: 'center', justifyContent: 'center',
  },
  missionCtaText: { fontFamily: theme.fonts.bold, fontSize: 14, color: '#fff' },

  // Intro card
  introCard: {
    backgroundColor: SURFACE,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: SURFACE3,
    padding: 20,
    gap: 14,
  },
  introHeaderRow: { flexDirection: 'row-reverse' },
  introTag: {
    fontFamily: theme.fonts.bold, fontSize: 11,
    letterSpacing: 0.6, textTransform: 'uppercase' as any,
    textAlign: 'right',
  },
  introText: {
    fontFamily: theme.fonts.regular, fontSize: 15, color: TEXT,
    textAlign: 'right', lineHeight: 26,
  },
  goalBox: {
    backgroundColor: SURFACE2, borderRadius: 12, padding: 14, gap: 5,
    borderWidth: 1, borderColor: `${GOLD}2A`,
  },
  goalLabel: {
    fontFamily: theme.fonts.bold, fontSize: 10, color: GOLD,
    textAlign: 'right', letterSpacing: 0.4, textTransform: 'uppercase' as any,
  },
  goalText: {
    fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED,
    textAlign: 'right', lineHeight: 22,
  },
  dismissBtn: {
    borderRadius: 13, paddingVertical: 14,
    alignItems: 'center', justifyContent: 'center',
  },
  dismissBtnText: { fontFamily: theme.fonts.bold, fontSize: 15, color: '#fff' },

  persistenceCard: {
    backgroundColor: '#121126',
    borderWidth: 1,
    borderColor: SURFACE3,
    borderRadius: 12,
    padding: 12,
    gap: 6,
  },
  persistenceTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 13, textAlign: 'right' },
  persistenceBody: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12, lineHeight: 18, textAlign: 'right' },

  // Sections + action cards
  sectionHeaderWrap: { paddingTop: 8, gap: 4 },
  sectionTitle: {
    fontFamily: theme.fonts.bold, fontSize: 15, color: TEXT,
    textAlign: 'right',
  },
  sectionSubtitle: {
    fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MUTED, textAlign: 'right',
  },
  primaryFocusCard: {
    backgroundColor: SURFACE,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: SURFACE3,
    padding: 14,
    gap: 8,
  },
  primaryFocusEyebrow: {
    color: TEXT_MUTED,
    fontFamily: theme.fonts.bold,
    fontSize: 11,
    textAlign: 'right',
    letterSpacing: 0.4,
  },
  primaryFocusTitle: {
    color: TEXT,
    fontFamily: theme.fonts.bold,
    fontSize: 16,
    textAlign: 'right',
    lineHeight: 24,
  },
  primaryFocusBody: {
    color: TEXT_MED,
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    textAlign: 'right',
    lineHeight: 20,
  },
  primaryFocusBtn: {
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  primaryFocusBtnText: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 14 },
  secondaryGrid: { gap: 10 },
  supportGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 10,
  },
  actionCard: {
    backgroundColor: SURFACE,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: SURFACE3,
    padding: 16,
    gap: 8,
    minHeight: 146,
    flexGrow: 1,
    flexBasis: Platform.OS === 'web' ? '48%' : '100%',
  },
  actionCardPrimary: {
    borderWidth: 1.5,
  },
  actionCardQuiet: {
    backgroundColor: SURFACE2,
  },
  actionIconRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionIcon: { fontSize: 17 },
  actionPriorityTag: {
    borderWidth: 1,
    borderRadius: 99,
    paddingHorizontal: 10,
    paddingVertical: 3,
    fontSize: 11,
    fontFamily: theme.fonts.bold,
  },
  actionTitle: {
    color: TEXT,
    fontFamily: theme.fonts.bold,
    fontSize: 17,
    textAlign: 'right',
  },
  actionBody: {
    color: TEXT_MED,
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'right',
  },
  actionCta: {
    fontFamily: theme.fonts.bold,
    fontSize: 14,
    textAlign: 'right',
    marginTop: 'auto',
  },
  emptyLessonsCard: {
    backgroundColor: SURFACE,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: SURFACE3,
    padding: 16,
    gap: 10,
  },
  emptyLessonsTitle: {
    color: TEXT,
    fontFamily: theme.fonts.bold,
    fontSize: 16,
    textAlign: 'right',
  },
  emptyLessonsBody: {
    color: TEXT_MED,
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    lineHeight: 21,
    textAlign: 'right',
  },
  emptyLessonsBtn: {
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 11,
    marginTop: 2,
    backgroundColor: `${PRIMARY}10`,
  },
  emptyLessonsBtnText: {
    fontFamily: theme.fonts.bold,
    fontSize: 14,
  },

  // Lesson row
  lessonRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 14,
    borderRadius: 16,
    borderWidth: 1,
    paddingVertical: 14,
    paddingHorizontal: 14,
    ...(Platform.OS === 'web' ? ({ transition: 'opacity 0.12s' } as any) : {}),
  },
  lessonDot: {
    width: 40, height: 40, borderRadius: 20, borderWidth: 1.5,
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  lessonDotIcon: { fontSize: 14, fontWeight: '700' as any },
  lessonContent: { flex: 1, gap: 4 },
  lessonTitle:   { fontFamily: theme.fonts.bold, fontSize: 15, textAlign: 'right', lineHeight: 22 },
  lessonTitleLocked: { opacity: 0.5 },
  lessonDesc:    {
    fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MUTED,
    textAlign: 'right', lineHeight: 17,
  },
  lessonMeta:    { flexDirection: 'row-reverse', gap: 6, alignItems: 'center', flexWrap: 'wrap' },
  lessonMetaItem: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MUTED },
  lessonEarned:  { fontFamily: theme.fonts.bold, fontSize: 11, color: GREEN, textAlign: 'right' },
  lessonArrow:   { fontSize: 24, fontWeight: '300' as any, flexShrink: 0, opacity: 0.8 },

  // Leaderboard
  lbCard: {
    backgroundColor: SURFACE,
    borderRadius: 18,
    borderWidth: 1,
    padding: 18,
    gap: 2,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 4px 16px rgba(0,0,0,0.3)' } as any) : {}),
  },
  lbTitle: {
    fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT_MUTED,
    textAlign: 'right', marginBottom: 8, letterSpacing: 0.5,
    textTransform: 'uppercase' as any,
  },
  lbRow: {
    flexDirection: 'row-reverse', alignItems: 'center',
    gap: 10, paddingVertical: 8, paddingHorizontal: 8,
  },
  lbRank: { fontFamily: theme.fonts.bold, fontSize: 15, width: 32, textAlign: 'center' },
  lbName: {
    flex: 1, fontFamily: theme.fonts.regular,
    fontSize: 14, color: TEXT, textAlign: 'right',
  },
  lbXp: { fontFamily: theme.fonts.bold, fontSize: 12, color: GOLD },
});
